import React, { useEffect, useState } from 'react'

import { InvoiceBatchLocale, InvoiceBatchParams, getVendorCodes } from '../../../api'
import { JsonVendorCode, VendorCode } from '../../../models/vendorCode'
import { getAuthUser } from '../../../helpers'
import defineAbilityFor from '../defineAbility'


interface FilterProps {
    updateFilters: <K extends keyof InvoiceBatchParams>(filterName: K, newValue: InvoiceBatchParams[K], shouldDelete?: boolean) => void
    billingTypePermissions?: boolean
}

export const InvoiceBatchFilters = (props: FilterProps) => {
    const { updateFilters, billingTypePermissions = false } = props

    const [vendorCodes, setVendorCodes] = useState([])
    const [dateFrom, setDateFrom] = useState<InvoiceBatchParams['check_in_date_from']>('')
    const [dateTo, setDateTo] = useState<InvoiceBatchParams['check_in_date_to']>('')
    const [minimumCommission, setMinimumCommission] = useState<InvoiceBatchParams['minimum_commission']>(10)
    const [maximumCommission, setMaximumCommission] = useState<InvoiceBatchParams['maximum_commission']>(20000)
    const [locale, setLocale] = useState<InvoiceBatchParams['locale']>('domestic_locales')
    const [minPages, setMinPages] = useState<InvoiceBatchParams['minimum_pages']>(1)
    const [maxPages, setMaxPages] = useState<InvoiceBatchParams['maximum_pages']>(50)
    const [vendorId, setVendorId] = useState<InvoiceBatchParams['vendor_code']>(-1)
    const [billingType, setBillingType] = useState<string>('-1')

    const ability = defineAbilityFor(getAuthUser())
    const canUseDocupost = ability.can('Create', 'InvoiceBillingDocupost')

    const restrictBillingType = billingTypePermissions && !canUseDocupost

    useEffect(() => {
        getVendorCodes()
            .then(json => setVendorCodes(
                json.data.map((vendor: JsonVendorCode) => new VendorCode(vendor))
            ))
    }, [])

    useEffect(() => {
        if (locale === 'international_locales') {
            setMinimumCommission(100)
            updateFilters(
                'minimum_commission',
                100
            )
        } else {
            setMinimumCommission(10)
            updateFilters(
                'minimum_commission',
                10
            )
        }
    }, [locale])

    useEffect(() => {
        if (restrictBillingType) {
            setBillingType('e_billing_suppliers')
            updateFilters('billing_type', 'e_billing_suppliers')
        }
    }, [restrictBillingType])

    return (
        <div>
            <div className="row g-2 mb-3">
                <div className="col-md-6">
                    <label htmlFor='checkInDateFromFilter'>Guest Check-in Date (From)</label>
                    <input
                        className='form-control'
                        id='checkInDateFromFilter'
                        onChange={(e) => {
                            updateFilters(
                                'check_in_date_from',
                                e.target.value ?? '',
                                e.target.value === ''
                            )

                            setDateFrom(e.target.value)
                        }}
                        type='date'
                        max={dateTo} />
                </div>
                <div className="col-md-6">
                    <label htmlFor='checkInDateToFilter'>Guest Check-in Date (To)</label>
                    <input
                        className='form-control'
                        id='checkInDateToFilter'
                        onChange={(e) => {
                            updateFilters(
                                'check_in_date_to',
                                e.target.value ?? '',
                                e.target.value === ''
                            )

                            setDateTo(e.target.value)
                        }}
                        type='date'
                        min={dateFrom} />
                </div>
                <div className="col-md-4">
                    <label htmlFor='localeFilter'>Locale</label>
                    <select
                        className='form-select'
                        id='localeFilter'
                        onChange={(e) => {
                            const locale = e.target.value as InvoiceBatchLocale
                            updateFilters('locale', locale)
                            setLocale(locale)
                        }}
                        defaultValue={'domestic_locales'}>
                        <option value={'domestic_locales'}>
                            Domestic
                        </option>
                        <option value={'international_locales'}>
                            International
                        </option>
                    </select>
                </div>
                <div className="col-md-8">
                    <label htmlFor='vendorFilter'>Vendor ID</label>
                    <select
                        className='form-select'
                        id='vendorFilter'
                        onChange={(e) => {
                            const newValue = Number(e.target.value)
                            updateFilters(
                                'vendor_code',
                                newValue === -1 ? undefined : newValue,
                                newValue === -1
                            )
                            setVendorId(newValue)
                        }}>
                        <option value={-1}>
                            All Vendor Codes
                        </option>
                        {vendorCodes.map((vendor: VendorCode) => (
                            <option key={vendor.id} value={vendor.id}>
                                {vendor.code}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {(vendorId === -1) && (
                <div className="row g-2 mb-3">
                    <div className="col-md-12">
                        <label htmlFor='billingTypeFilter'>Billing Type</label>
                        <select
                            className='form-select'
                            id='billingTypeFilter'
                            value={billingType}
                            onChange={(e) => {
                                const value = e.target.value
                                updateFilters(
                                    'billing_type',
                                    value !== '-1' ? value : undefined,
                                    value === '-1'
                                )
                                setBillingType(value)
                            }}>
                            <option value={'-1'}
                                disabled={restrictBillingType}>
                                All Billing Types
                            </option>
                            <option value={'e_billing_suppliers'}>
                                E-billing Suppliers
                            </option>
                            <option
                                value={'docupost_suppliers'}
                                disabled={restrictBillingType}
                            >
                                DocuPost Suppliers
                            </option>
                        </select>
                    </div>
                </div>
            )}

            <div className="row g-2 mb-3">
                <div className="col-md-3">
                    <label htmlFor='minimumPagesFilter'>Minimum Pages</label>
                    <input
                        className='form-control'
                        id='minimumPagesFilter'
                        onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : undefined
                            updateFilters(
                                'minimum_pages',
                                newValue,
                                e.target.value === ''
                            )
                            setMinPages(newValue)
                        }}
                        type='number'
                        value={minPages}
                        max={maxPages}
                        min={0} />
                </div>
                <div className="col-md-3">
                    <label htmlFor='maximumPagesFilter'>Maximum Pages</label>
                    <input
                        className='form-control'
                        id='maximumPagesFilter'
                        onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : undefined
                            updateFilters(
                                'maximum_pages',
                                newValue,
                                e.target.value === ''
                            )
                            setMaxPages(newValue)
                        }}
                        type='number'
                        value={maxPages}
                        min={(Number(minPages) >= 0) ? minPages : 0} />
                </div>
                <div className="col-md-3">
                    <label htmlFor='minimumCommissionFilter'>Minimum Commission</label>
                    <input
                        className='form-control'
                        id='minimumCommissionFilter'
                        onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : undefined
                            updateFilters(
                                'minimum_commission',
                                newValue,
                                e.target.value === ''
                            )
                            setMinimumCommission(newValue)
                        }}
                        value={minimumCommission}
                        type='number'
                        min={0} />
                </div>
                <div className="col-md-3">
                    <label htmlFor='maxCommission'>Maximum Commission</label>
                    <input
                        className='form-control'
                        id='maxCommission'
                        onChange={(e) => {
                            const newValue = e.target.value ? Number(e.target.value) : undefined
                            updateFilters(
                                'maximum_commission',
                                newValue,
                                e.target.value === ''
                            )
                            setMaximumCommission(newValue)
                        }}
                        value={maximumCommission}
                        type='number'
                        min={(Number(minimumCommission) >= 0) ? minimumCommission : 0} />
                </div>
            </div>

            <div className="row g-2">
                <div className="col-md-12">
                    <label htmlFor='excludeInvoicedAfterFilter'>Exclude Records Invoiced After</label>
                    <input
                        className='form-control'
                        id='excludeInvoicedAfterFilter'
                        onChange={(e) => {
                            updateFilters(
                                'exclude_invoiced_after',
                                e.target.value ?? '',
                                e.target.value === ''
                            )
                        }}
                        type='date' />
                </div>
            </div>
        </div>
    )
}
